import { useState } from 'react';
import { useRouter } from 'next/navigation';
import type { Tab, SubTab } from './navbar-types';
import { useAuth } from '@/hooks/useAuth';
import { NavbarMobileLoginButton } from './NavbarMobileLoginButton';
import { NavbarMobileCreateAccountButton } from './NavbarMobileCreateAccountButton';

interface NavbarMobileMenuProps {
  tabs: Tab[];
  isAuthenticated: boolean;
  profileSubtabs: SubTab[];
  onLinkClick: () => void;
}

const handleLinkClick = (
  e: React.MouseEvent,
  href: string,
  internal: boolean | undefined,
  navigate: (path: string) => void,
  onLinkClick: () => void,
) => {
  if (href === '#') e.preventDefault();
  else if (internal) {
    e.preventDefault();
    navigate(href);
  }
  onLinkClick();
};

export const NavbarMobileMenu = ({
  tabs,
  isAuthenticated,
  profileSubtabs,
  onLinkClick,
}: NavbarMobileMenuProps) => {
  const router = useRouter();
  const navigate = (path: string) => router.push(path);
  const { logout } = useAuth();
  const [expandedSubtabs, setExpandedSubtabs] = useState<Set<string>>(() => new Set());

  const toggleSubtab = (label: string) => {
    setExpandedSubtabs((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <div className="navbar-mobile-menu">
      {tabs.map((tab) =>
        tab.subtabs ? (
          <div key={tab.label} className="navbar-mobile-subtab-expandable">
            <button
              type="button"
              className="navbar-mobile-tab"
              onClick={() => toggleSubtab(tab.label)}
              aria-expanded={expandedSubtabs.has(tab.label)}
            >
              <span>{tab.label}</span>
              <i className="fa fa-angle-down navbar-mobile-tab-icon" aria-hidden />
            </button>
            {expandedSubtabs.has(tab.label) && (
              <div className="navbar-mobile-subtab-menu">
                {tab.subtabs.map((subtab) => (
                  <a
                    key={subtab.label}
                    href={subtab.href}
                    className="navbar-mobile-tab navbar-mobile-subtab"
                    onClick={(e) =>
                      handleLinkClick(e, subtab.href, subtab.internal, navigate, onLinkClick)
                    }
                  >
                    <span>{subtab.label}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        ) : (
          <a
            key={tab.label}
            href={tab.href ?? '#'}
            className="navbar-mobile-tab"
            onClick={(e) =>
              handleLinkClick(e, tab.href ?? '#', tab.internal, navigate, onLinkClick)
            }
          >
            <span>{tab.label}</span>
          </a>
        ),
      )}
      <div className="navbar-mobile-tab-spacer" />
      {isAuthenticated ? (
        <>
          {profileSubtabs.map((subtab) =>
            subtab.subtabs ? (
              <div key={subtab.label} className="navbar-mobile-subtab-expandable">
                <button
                  type="button"
                  className="navbar-mobile-tab"
                  onClick={() => toggleSubtab(subtab.label)}
                  aria-expanded={expandedSubtabs.has(subtab.label)}
                >
                  <span>{subtab.label}</span>
                  <i className="fa fa-angle-down navbar-mobile-tab-icon" aria-hidden />
                </button>
                {expandedSubtabs.has(subtab.label) && (
                  <div className="navbar-mobile-subtab-menu">
                    {subtab.subtabs.map((nested) => (
                      <a
                        key={nested.label}
                        href={nested.href}
                        className="navbar-mobile-tab navbar-mobile-subtab"
                        onClick={(e) =>
                          handleLinkClick(e, nested.href, nested.internal, navigate, onLinkClick)
                        }
                      >
                        <span>{nested.label}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <a
                key={subtab.label}
                href={subtab.href}
                className="navbar-mobile-tab"
                onClick={(e) =>
                  handleLinkClick(e, subtab.href, subtab.internal, navigate, onLinkClick)
                }
              >
                <span>{subtab.label}</span>
              </a>
            ),
          )}
          <hr className="navbar-mobile-tab-divider" />
          <a
            href="#"
            className="navbar-mobile-tab"
            onClick={(e) => {
              e.preventDefault();
              logout();
              onLinkClick();
            }}
          >
            <span>Log Out</span>
          </a>
        </>
      ) : (
        <div className="navbar-mobile-auth-buttons">
          <NavbarMobileLoginButton />
          <NavbarMobileCreateAccountButton />
        </div>
      )}
    </div>
  );
};
